import React, {createContext, useContext, useReducer, useEffect, useMemo} from "react";
import debounce from 'lodash/debounce';
import axios from "../api/axios";

const initialState = {
    // Step tracking
    currentStep: 1, // Changed to start at 1 for consistency
    totalSteps: 5,
  
    // Basic details (Step 1)
    name: '',
    email: '',
    phno: '',
    isEmailVerified: false,
    generatedUserId: '',
  
    // Account creation (Step 2)
    password: '',
    confirmPassword: '',
  
    // User type & educational level (Step 3)
    userType: '', // 'Students', 'Parents', etc.
    educationLevel: '', // 'Below8th', 'Bachelors', etc.
  
    // Personal details (Step 4)
    firstname: '',
    lastname: '',
    gender: '',
    country: '',
    city: '',
    homestate: '',
    pin: '',
  
    // Educational details (Step 5 - conditional based on educationLevel)
    educationalDetails: {},
  
    // Meta information
    formErrors: {},
    isSubmitting: false,
    isCompleted: false,
    access_token: "",
  };

  const actionTypes = {
    SET_FIELD: 'SET_FIELD',
    SET_MULTIPLE_FIELDS: 'SET_MULTIPLE_FIELDS',
    NEXT_STEP: 'NEXT_STEP',
    PREV_STEP: 'PREV_STEP',
    GO_TO_STEP: 'GO_TO_STEP',
    SET_FORM_ERROR: 'SET_FORM_ERROR',
    CLEAR_FORM_ERROR: 'CLEAR_FORM_ERROR',
    RESET_FORM: 'RESET_FORM',
    SET_EDUCATIONAL_DETAILS: 'SET_EDUCATIONAL_DETAILS',
  };

  const registrationReducer = (state, action) => {
    switch (action.type) {
      case actionTypes.SET_FIELD:
        return {
          ...state,
          [action.field]: action.value,
        };
  
      case actionTypes.SET_MULTIPLE_FIELDS:
        return {
          ...state,
          ...action.fields,
        };
  
      case actionTypes.NEXT_STEP:
        return {
          ...state,
          currentStep: Math.min(state.currentStep + 1, state.totalSteps),
        };
  
      case actionTypes.PREV_STEP:
        return {
          ...state,
          currentStep: Math.max(state.currentStep - 1, 1),
        };
  
      case actionTypes.GO_TO_STEP:
        return {
          ...state,
          currentStep: Math.max(1, Math.min(action.step, state.totalSteps)),
        };
  
      case actionTypes.SET_FORM_ERROR:
        return {
          ...state,
          formErrors: {
            ...state.formErrors,
            [action.field]: action.error,
          },
        };
  
      case actionTypes.CLEAR_FORM_ERROR:
        const newErrors = { ...state.formErrors };
        delete newErrors[action.field];
        return {
          ...state,
          formErrors: newErrors,
        };
  
      case actionTypes.RESET_FORM:
        return initialState;
  
      case actionTypes.SET_EDUCATIONAL_DETAILS:
        return {
          ...state,
          educationalDetails: {
            ...state.educationalDetails,
            ...action.details,
          },
        };
  
      default:
        return state;
    }
  };

  const RegistrationContext = createContext();

  export const useRegistration = () => {
    const context = useContext(RegistrationContext);
    if (!context) {
      throw new Error('useRegistration must be used within a RegistrationProvider');
    }
    return context;
  };

  export const RegistrationProvider = ({children}) =>{
    const loadInitialState = () =>{
        const savedState = localStorage.getItem('registrationState');
        return savedState ? JSON.parse(savedState) : initialState;
    };

    const [state, dispatch] = useReducer(registrationReducer, loadInitialState());

    const debouncedSave = useMemo(
      () => debounce((currentState) => {
        const { password, confirmPassword, ...stateToSave } = currentState;
        localStorage.setItem('registrationState', JSON.stringify(stateToSave));
      }, 500),
      []
    );

    useEffect(() => {
        debouncedSave(state);
        return () => debouncedSave.cancel(); // Cleanup on unmount
      }, [state, debouncedSave]);

      const setField = (field, value) => {
        dispatch({ type: actionTypes.SET_FIELD, field, value });
      };
    
      const setMultipleFields = (fields) => {
        dispatch({ type: actionTypes.SET_MULTIPLE_FIELDS, fields });
      };
    
      const nextStep = () => {
        dispatch({ type: actionTypes.NEXT_STEP });
      };
    
      const prevStep = () => {
        if (state.currentStep === 3 && state.generatedUserId) {
          // Try to delete user using URL parameter approach
          console.log(`Attempting to delete user: ${state.generatedUserId}`);
          
          // First try the DELETE endpoint
          axios.delete(`/api/deleteUser/${state.generatedUserId}`)
            .then((response) => {
              console.log("User deletion response:", response.data);
              dispatch({ type: actionTypes.GO_TO_STEP, step: 1 });
              resetForm();
            })
            .catch(err => {
              console.error("DELETE request failed:", err.response?.data || err.message);
              
              // Fall back to the GET endpoint if DELETE fails
              console.log("Trying alternative GET endpoint for deletion");
              
              axios.get(`/api/deleteUser/remove/${state.generatedUserId}`)
                .then((response) => {
                  console.log("User deletion via GET response:", response.data);
                  dispatch({ type: actionTypes.GO_TO_STEP, step: 1 });
                  resetForm();
                })
                .catch(secondErr => {
                  console.error("Both deletion attempts failed:", secondErr.response?.data || secondErr.message);
                  // Still go back and reset even if both attempts fail
                  dispatch({ type: actionTypes.GO_TO_STEP, step: 1 });
                  resetForm();
                });
            });
        } else {
          dispatch({ type: actionTypes.PREV_STEP });
        }
      };
    
      const goToStep = (step) => {
        dispatch({ type: actionTypes.GO_TO_STEP, step });
      };
    
      const setFormError = (field, error) => {
        dispatch({ type: actionTypes.SET_FORM_ERROR, field, error });
      };
    
      const clearFormError = (field) => {
        dispatch({ type: actionTypes.CLEAR_FORM_ERROR, field });
      };
    
      const resetForm = () => {
        dispatch({ type: actionTypes.RESET_FORM });
        localStorage.removeItem('registrationState');
      };
    
      const setEducationalDetails = (details) => {
        dispatch({ type: actionTypes.SET_EDUCATIONAL_DETAILS, details });
      };
    
      // Generate a user ID based on name and a random number
      const generateUserId = (name) => {
        const namePart = name.toLowerCase().replace(/[^a-z0-9]/g, '').substring(0, 8);
        const randomPart = Math.floor(1000 + Math.random() * 9000); // 4-digit random number
        return `${namePart}${randomPart}`;
      };
    
      // Validation schemas per step (simplified, schema-like approach)
      const validateStep = (step) => {
        let isValid = true;
        const errors = {};
    
        switch (step) {
          case 1: // Basic details
            if (!state.name.trim()) errors.name = 'Name is required';
            if (!state.email.trim()) {
              errors.email = 'Email is required';
            } else if (!/\S+@\S+\.\S+/.test(state.email)) {
              errors.email = 'Email is invalid';
            }
            if (!state.phno.trim()) errors.phno = 'Phone number is required';
            isValid = Object.keys(errors).length === 0;
            break;
    
          case 2: // Account creation
            if (!state.password) {
              errors.password = 'Password is required';
            } else if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%+]).{8,24}$/.test(state.password)) {
              errors.password =
                'Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character';
            }
            if (state.password !== state.confirmPassword) {
              errors.confirmPassword = 'Passwords do not match';
            }
            isValid = Object.keys(errors).length === 0;
            break;
    
          case 3: // User type & education level
            if (!state.userType) errors.userType = 'User type is required';
            if (state.userType === 'Students' && !state.educationLevel) {
              errors.educationLevel = 'Education level is required';
            }
            isValid = Object.keys(errors).length === 0;
            break;
    
          case 4: // Personal details
            if (!state.firstname.trim()) errors.firstname = 'First name is required';
            if (!state.lastname.trim()) errors.lastname = 'Last name is required';
            if (!state.gender) errors.gender = 'Gender is required';
            if (!state.country.trim()) errors.country = 'Country is required';
            if (!state.city.trim()) errors.city = 'City is required';
            isValid = Object.keys(errors).length === 0;
            break;
    
          case 5: // Educational details (optional, based on educationLevel)
            // Add specific validation if needed
            isValid = true;
            break;
    
          default:
            isValid = true;
        }
    
        // Clear existing errors and set new ones
        Object.keys(state.formErrors).forEach((field) => clearFormError(field));
        Object.entries(errors).forEach(([field, error]) => setFormError(field, error));
    
        return isValid;
      };
      const value = useMemo(
        () => ({
          ...state,
          setField,
          setMultipleFields,
          nextStep,
          prevStep,
          goToStep,
          setFormError,
          clearFormError,
          resetForm,
          setEducationalDetails,
          generateUserId,
          validateStep,
          actionTypes,
        }),
        [state] // Only re-compute when state changes
      );
    
      return <RegistrationContext.Provider value={value}>{children}</RegistrationContext.Provider>;
    };
    